import { useQuery } from '@tanstack/react-query';

import type { OrderTimeline } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get order timeline (notes) by ID
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 * @param timezone - Optional IANA timezone string (defaults to browser timezone)
 */
export const getOrderTimeline = async (
	servicesProviderId: string,
	orderId: string,
	timezone?: string,
): Promise<OrderTimeline> => {
	const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await apiClient.get<OrderTimeline>(
		ordersEndpoints.timeline(servicesProviderId, orderId),
		{ params: { timezone: tz } },
	);
	return response.data;
};

/**
 * Hook for fetching order timeline (notes) by ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetOrderTimeline = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.timeline.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to fetch order timeline without an order ID.');
			}
			return getOrderTimeline(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: 60000, // 1 minute
		...errorBoundaryOptions,
	});
};
